import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"
import {
  aggregateScalarMonthPoints,
  currentPeriodProgress,
  fillMonthBuckets,
  findInflightPeriodIndex,
  formatMonthCategory,
  linearPeriodProjection,
  monthRangeEndingAt,
  monthRangeForYear,
  monthRangeFromFirstDataToToday,
  periodBounds,
  resolveTimelineGranularity,
  timelinePointsToMonthBuckets
} from "../utils/chart_timeline_aggregation"
import { monotoneCubicSegmentPath } from "../utils/monotone_cubic_path"
import {
  apexChartGridPadding,
  apexTooltipOptions,
  apexDataLabelPill,
  observeChartTheme,
  observeCompactChartViewport,
  readChartTheme
} from "../utils/chart_theme"
import {
  hideCrosshair,
  markerCenter,
  seriesPixelPointsFromMarkers,
  syncCrosshairToMarker
} from "../utils/chart_geometry"
import { buildChartIndexFilterUrl, visitChartIndexFilterUrl } from "../utils/chart_index_filter_url"
import {
  clearTooltipPositioned,
  positionApexTooltip
} from "../utils/apex_tooltip_position"

const MONTH_SHORT_FR = [
  "janv.", "févr.", "mars", "avr.", "mai", "juin",
  "juil.", "août", "sept.", "oct.", "nov.", "déc."
]

const INFLIGHT_DATA_LABEL_OFFSET_Y = 32
const DEFAULT_DATA_LABEL_OFFSET_Y = -10
const INFLIGHT_DATA_LABEL_DELTA_Y = INFLIGHT_DATA_LABEL_OFFSET_Y - DEFAULT_DATA_LABEL_OFFSET_Y
const PROJECTION_LINE_COLOR = "#98A2B3"

export default class extends Controller {
  static targets = ["chart", "rangeButton"]
  static values = {
    payload: Object,
    selectedRange: String
  }

  connect() {
    this.unobserveTheme = observeChartTheme(() => this.renderChart())
    this.unobserveCompactViewport = observeCompactChartViewport(() => this.renderChart())
    this.renderWhenVisible()
  }

  // Le graphique vit dans un onglet Bootstrap du tableau de bord : au premier chargement son
  // conteneur est souvent masqué, donc large de 0. ApexCharts ne joue son animation de tracé
  // que si la grille a une largeur, et marque l'instance « redimensionnée » dès qu'il la
  // remesure lui-même — l'animation est alors perdue pour de bon, y compris au moment où
  // l'onglet s'ouvre. On attend donc que le conteneur soit réellement visible pour monter le
  // graphique. (Un ResizeObserver ne suffit pas : il ne se déclenche pas sur la sortie de
  // `display: none` ici.)
  renderWhenVisible() {
    if (!this.hasChartTarget) return
    if (typeof IntersectionObserver === "undefined" || this.chartTarget.clientWidth > 0) {
      this.scheduleRender()
      return
    }

    this.visibilityObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      this.visibilityObserver.disconnect()
      this.visibilityObserver = null
      this.scheduleRender()
    })
    this.visibilityObserver.observe(this.chartTarget)
  }

  scheduleRender() {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.rafId = requestAnimationFrame(() => {
      this.rafId = requestAnimationFrame(() => this.renderChart())
    })
  }

  disconnect() {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.unobserveTheme?.()
    this.unobserveCompactViewport?.()
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect()
      this.visibilityObserver = null
    }
    this.destroyChart()
  }

  selectRange(event) {
    const range = event.currentTarget.dataset.range
    if (!range || range === this.selectedRangeValue) return

    this.selectedRangeValue = range
    this.syncRangeButtons()
    this.renderChart()
  }

  renderChart() {
    if (!this.hasChartTarget) return

    try {
      this.destroyChart()
      // ApexCharts empile ses `.apexcharts-canvas` (`el.appendChild`) et ne vide jamais son
      // conteneur ; `destroyChart` ne sait défaire que l'instance montée par CE contrôleur.
      // Le corps du tableau de bord étant greffé d'une visite à l'autre, un graphique hérité
      // n'a plus de propriétaire : sans ce vidage, il reste et le nouveau se pose en dessous.
      this.chartTarget.replaceChildren()
      this.chartInstance = new ApexCharts(this.chartTarget, this.buildOptions())
      this.chartInstance.render().then(() => this.afterLineChartDraw(this.chartInstance))
      this.syncRangeButtons()
    } catch (error) {
      console.error("[apex-dashboard-chart] render failed", error)
    }
  }

  destroyChart() {
    if (this.chartInstance) {
      this.chartInstance.destroy()
      this.chartInstance = null
    }
    this.lineProjection = null
    this.lineProjectionColor = null
  }

  syncRangeButtons() {
    if (!this.hasRangeButtonTarget) return

    this.rangeButtonTargets.forEach((button) => {
      const active = button.dataset.range === this.selectedRangeValue
      button.classList.toggle("depozen-ui-filter-pill--active", active)
      button.classList.toggle("bg-primary", active)
      button.classList.toggle("text-white", active)
      button.classList.toggle("depozen-ui-filter-pill--inactive", !active)
      button.setAttribute("aria-pressed", active ? "true" : "false")
    })
  }

  buildOptions() {
    const payload = this.payloadValue || {}
    return payload.kind === "line" ? this.buildLineOptions(payload) : this.buildBarOptions(payload)
  }

  buildLineOptions(payload) {
    const theme = readChartTheme()
    const color = payload.color || "#198754"
    const view = this.lineView(payload.points || [], payload.indexFilter)
    this.lineViewState = view
    const projection = view.projection
    const projectionLabel = payload.labels?.projection || "Projection fin de période"
    const manyPoints = view.categories.length > 18
    const dataLabelPill = apexDataLabelPill(color, theme)
    const projectionColor = PROJECTION_LINE_COLOR
    const numericValues = view.values.filter((value) => value != null && !Number.isNaN(value))
    const dataMin = numericValues.length ? Math.min(...numericValues) : 0
    const dataMax = numericValues.length
      ? Math.max(...numericValues, projection?.projectedValue ?? 0)
      : 1
    const niceTicks = this.buildNiceTicks(dataMin, dataMax, 2)
    const valueSpan = Math.max(dataMax - dataMin, 1)
    const series = [{ name: payload.title || "Série", type: "area", data: view.values }]
    const seriesTitle = payload.title || "Série"
    this.lineProjection = projection
    this.lineProjectionColor = projection ? projectionColor : null

    return {
      chart: {
        type: "line",
        height: 280,
        width: "100%",
        fontFamily: "var(--headings-font, 'Space Grotesk', BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif)",
        toolbar: { show: false },
        zoom: manyPoints ? { enabled: true, type: "x", allowMouseWheelZoom: false } : { enabled: false },
        animations: { enabled: true, speed: 450 },
        parentHeightOffset: 0,
        events: this.lineChartEvents()
      },
      series,
      colors: [color],
      stroke: {
        curve: "monotoneCubic",
        width: 3,
        colors: [color]
      },
      fill: {
        type: "gradient",
        opacity: 1,
        gradient: {
          type: "vertical",
          shadeIntensity: 0.15,
          opacityFrom: 0.35,
          opacityTo: 0.02,
          stops: [0, 90, 100]
        }
      },
      plotOptions: {},
      dataLabels: {
        enabled: !manyPoints,
        offsetY: -10,
        style: {
          fontSize: "11px",
          fontWeight: 700,
          colors: [dataLabelPill.textColor]
        },
        background: dataLabelPill.background,
        formatter: (value) => (value == null ? "" : String(value))
      },
      markers: {
        size: manyPoints ? 3 : 5,
        colors: [color],
        strokeColors: [theme.bodyBg],
        strokeWidth: 2,
        hover: { size: 6 }
      },
      grid: {
        borderColor: theme.gridColor,
        xaxis: { lines: { show: false } },
        padding: apexChartGridPadding(
          { top: 16, right: 8, bottom: 0, left: 8 },
          { top: 6, right: 0, bottom: 0, left: 0 }
        )
      },
      xaxis: {
        categories: view.categories,
        tickPlacement: "on",
        axisBorder: { show: false },
        axisTicks: { show: false },
        crosshairs: {
          stroke: { color: theme.gridColor, dashArray: 4 }
        },
        labels: {
          rotate: manyPoints ? -45 : 0,
          rotateAlways: manyPoints,
          hideOverlappingLabels: true,
          style: { colors: theme.axisLabelColor, fontSize: manyPoints ? "10px" : "12px", fontWeight: 500 }
        }
      },
      yaxis: {
        forceNiceScale: false,
        tickAmount: 3,
        labels: {
          style: { colors: theme.axisLabelColor, fontSize: "11px", fontWeight: 500 },
          formatter: (value) => this.axisLabelFor(value, niceTicks, valueSpan)
        }
      },
      legend: { show: false },
      tooltip: projection
        ? {
          ...apexTooltipOptions(theme),
          shared: true,
          intersect: false,
          followCursor: false,
          hideEmptySeries: true,
          x: { show: false },
          cssClass: "apex-dashboard-chart__apex-tooltip",
          custom: ({ series, dataPointIndex }) => {
            queueMicrotask(() => this.positionLineTooltip(dataPointIndex))
            return this.buildLineTooltipHtml({
              series,
              dataPointIndex,
              categories: view.categories,
              projection,
              projectionLabel,
              seriesTitle,
              color,
              projectionColor
            })
          }
        }
        : {
          ...apexTooltipOptions(theme),
          shared: true,
          intersect: false,
          followCursor: false,
          cssClass: "apex-dashboard-chart__apex-tooltip",
          custom: ({ series, dataPointIndex }) => {
            queueMicrotask(() => this.positionLineTooltip(dataPointIndex))
            const title = view.categories[dataPointIndex] || ""
            const value = series?.[0]?.[dataPointIndex]
            if (value == null || Number.isNaN(value)) return ""
            return `
              <div class="apexcharts-tooltip-title">${title}</div>
              <div class="apexcharts-tooltip-series-group" style="display: flex;">
                <span class="apexcharts-tooltip-marker" style="background-color: ${color};"></span>
                <div class="apexcharts-tooltip-text">
                  <span class="apexcharts-tooltip-text-y-label">${seriesTitle}: </span>
                  <span class="apexcharts-tooltip-text-y-value">${Number(value).toLocaleString("fr-FR")}</span>
                </div>
              </div>
            `
          }
        },
      states: {
        hover: { filter: { type: "none" } },
        active: { filter: { type: "none" } }
      }
    }
  }

  buildBarOptions(payload) {
    const theme = readChartTheme()
    const palette = payload.colors?.length ? payload.colors : [payload.color || "#198754"]
    const view = this.barView(payload.points || [])
    const barColors = view.values.map((_, index) => palette[index % palette.length])

    return {
      chart: {
        type: "bar",
        height: Math.max(280, 48 + view.categories.length * 28),
        width: "100%",
        fontFamily: "var(--headings-font, 'Space Grotesk', BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif)",
        toolbar: { show: false },
        animations: { enabled: true, speed: 450 },
        parentHeightOffset: 0
      },
      series: [{ name: payload.title || "Série", data: view.values }],
      colors: barColors,
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          borderRadiusApplication: "end",
          barHeight: "62%",
          distributed: true
        }
      },
      fill: { opacity: 1 },
      dataLabels: {
        enabled: true,
        style: { fontSize: "11px", fontWeight: 700, colors: [theme.bodyBg] },
        formatter: (value) => value.toLocaleString("fr-FR")
      },
      grid: {
        borderColor: theme.gridColor,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
        padding: apexChartGridPadding(
          { top: 0, right: 16, bottom: 0, left: 0 },
          { top: 0, right: 6, bottom: 0, left: 0 }
        )
      },
      xaxis: {
        categories: view.categories,
        labels: {
          style: { colors: theme.axisLabelColor, fontSize: "11px", fontWeight: 500 },
          formatter: (value) => Math.round(Number(value)).toLocaleString("fr-FR")
        },
        axisBorder: { show: false },
        axisTicks: { show: false }
      },
      yaxis: {
        labels: {
          style: { colors: theme.bodyColor, fontSize: "12px", fontWeight: 500 },
          maxWidth: 140
        }
      },
      legend: { show: false },
      tooltip: {
        ...apexTooltipOptions(theme),
        y: { formatter: (value) => value.toLocaleString("fr-FR") }
      },
      states: {
        hover: { filter: { type: "none" } },
        active: { filter: { type: "none" } }
      }
    }
  }

  lineView(points, indexFilter = null) {
    const normalized = points.map((point) => ({
      date: this.parseDate(point.date || point.label),
      value: point.value == null ? null : Number(point.value)
    })).filter((point) => point.date)

    const range = this.selectedRangeValue
    let filtered = normalized

    const monthData = timelinePointsToMonthBuckets(normalized, {
      valueKey: "value",
      getDate: (point) => point.date
    })
    const today = new Date()

    if (range === "12m") {
      const calendarMonths = monthRangeEndingAt(today, 12)
      const filled = fillMonthBuckets(calendarMonths, monthData)
      return this.monthBucketView(filled, { indexFilter })
    }

    if (/^\d{4}$/.test(range)) {
      const calendarMonths = monthRangeForYear(range, today)
      const inYear = monthData.filter((point) => String(point.year) === range)
      const filled = fillMonthBuckets(calendarMonths, inYear)
      return this.monthBucketView(filled, { indexFilter })
    }

    if (range === "all") {
      const calendarMonths = monthRangeFromFirstDataToToday(monthData, today)
      const filled = fillMonthBuckets(calendarMonths, monthData)
      const granularity = resolveTimelineGranularity(filled)
      const aggregated = granularity === "month"
        ? filled
        : aggregateScalarMonthPoints(filled, granularity)

      return this.withPeriodProjection({
        categories: aggregated.map((point) => formatMonthCategory(point)),
        values: aggregated.map((point) => point.value),
        buckets: aggregated,
        granularity,
        indexFilter
      })
    }

    return this.withPeriodProjection({
      categories: filtered.map((point) => this.formatMonthLabel(point.date)),
      values: filtered.map((point) => point.value),
      buckets: filtered.map((point) => ({
        year: point.date.getFullYear(),
        monthIndex: point.date.getMonth()
      })),
      granularity: "month",
      indexFilter
    })
  }

  monthBucketView(buckets, { indexFilter = null } = {}) {
    return this.withPeriodProjection({
      categories: buckets.map((point) => formatMonthCategory(point)),
      values: buckets.map((point) => point.value),
      buckets,
      granularity: "month",
      indexFilter
    })
  }

  withPeriodProjection(view) {
    const projection = this.computePeriodProjection(view)
    return projection ? { ...view, projection } : view
  }

  computePeriodProjection({ values, buckets, granularity }) {
    if (!values?.length || !buckets?.length) return null

    const projectionIndex = findInflightPeriodIndex(buckets, granularity, values)
    if (projectionIndex == null) return null

    const actualValue = values[projectionIndex]
    const bucket = buckets[projectionIndex]
    const progress = currentPeriodProgress(bucket, granularity)
    const projectedValue = linearPeriodProjection(actualValue, progress)

    if (projectedValue == null || projectedValue === actualValue) return null

    return {
      index: projectionIndex,
      actualValue,
      projectedValue,
      progress
    }
  }

  projectionPointPixels(instance, index) {
    const markerPoint = markerCenter(instance, index)
    if (markerPoint) return markerPoint

    const globals = instance?.w?.globals
    if (!globals) return null

    const x = globals.seriesXvalues?.[0]?.[index]
    const y = globals.seriesYvalues?.[0]?.[index]
    if (Number.isFinite(x) && Number.isFinite(y)) return { x, y }

    return null
  }

  projectionValueToPixel(instance, actualY, projection) {
    const globals = instance?.w?.globals
    if (!globals || actualY == null || !projection) return null

    const min = globals.minY
    const max = globals.maxY
    const range = max - min
    if (!Number.isFinite(range) || range <= 0) return null

    const valueDelta = projection.projectedValue - projection.actualValue
    return actualY - (valueDelta / range) * globals.gridHeight
  }

  seriesPixelPoints(instance) {
    const markerPoints = seriesPixelPointsFromMarkers(instance)
    if (markerPoints?.length) return markerPoints

    const globals = instance?.w?.globals
    const xs = globals?.seriesXvalues?.[0]
    const ys = globals?.seriesYvalues?.[0]
    if (!xs?.length || !ys?.length) return []

    return xs.map((x, index) => ({ x, y: ys[index] }))
      .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
  }

  lineChartEvents() {
    return {
      mounted: (ctx) => this.afterLineChartDraw(ctx),
      // ApexCharts surveille lui-même la fenêtre et la taille du parent, et repasse par
      // `updated` après chaque remesure : c'est là qu'on replace le tracé de projection.
      // Inutile (et nuisible) de doubler ça d'un ResizeObserver maison — remesurer soi-même
      // marque l'instance « redimensionnée », ce qui supprime l'animation de tracé.
      updated: (ctx) => this.afterLineChartDraw(ctx),
      zoomed: (ctx) => this.afterLineChartDraw(ctx),
      animationEnd: (ctx) => this.afterLineChartDraw(ctx),
      mouseMove: (_event, ctx, config) => {
        const instance = ctx?.chart ?? ctx
        const dataPointIndex = config?.dataPointIndex
        syncCrosshairToMarker(instance, dataPointIndex)
        if (dataPointIndex != null) this.positionLineTooltip(dataPointIndex, instance)
      },
      mouseLeave: (_event, ctx) => {
        const instance = ctx?.chart ?? ctx ?? this.chartInstance
        hideCrosshair(instance)
        clearTooltipPositioned(instance?.chart ?? instance)
      },
      markerClick: (_event, _ctx, { dataPointIndex }) => this.visitFilterUrl(dataPointIndex)
    }
  }

  positionLineTooltip(dataPointIndex, chart = this.chartInstance) {
    const instance = chart?.chart ?? chart
    const options = {
      mode: "line",
      offsetX: 16,
      verticalOffset: 10,
      markerPad: 10,
      dataLabelPad: 52,
      markerHalf: 12,
      gap: 8
    }
    positionApexTooltip(instance, dataPointIndex, options)
  }

  filterUrlForIndex(index) {
    const state = this.lineViewState
    if (!state?.indexFilter || index == null || !state.buckets?.[index]) return null

    return this.buildIndexFilterUrl(state.indexFilter, state.buckets[index], state.granularity)
  }

  visitFilterUrl(index) {
    visitChartIndexFilterUrl(this.filterUrlForIndex(index))
  }

  buildIndexFilterUrl(config, bucket, granularity = "month") {
    return buildChartIndexFilterUrl(config, bucket, granularity)
  }

  afterLineChartDraw(ctx) {
    const instance = ctx?.chart ?? ctx ?? this.chartInstance
    if (this.lineProjection) this.syncProjectionOverlay(instance)
    syncCrosshairToMarker(instance, null, { allowActiveFallback: true })
  }

  buildProjectionPath(instance, projection, projectedY) {
    const points = this.seriesPixelPoints(instance)
    if (points.length <= projection.index || projection.index < 1) return null

    const projectedPoints = points.map((point, index) => (
      index === projection.index ? { x: point.x, y: projectedY } : point
    ))

    return monotoneCubicSegmentPath(projectedPoints, projection.index)
  }

  syncProjectionOverlay(chart) {
    const instance = chart?.chart ?? chart ?? this.chartInstance
    const projection = this.lineProjection
    const svg = instance?.el?.querySelector?.("svg.apexcharts-svg")

    svg?.querySelector(".apex-dashboard-chart__projection-overlay")?.remove()
    if (!projection || !svg || !instance?.w?.globals) return

    const actualPoint = this.projectionPointPixels(instance, projection.index)
    if (!actualPoint) return

    const previousPoint = projection.index > 0
      ? this.projectionPointPixels(instance, projection.index - 1)
      : null
    if (!previousPoint) return

    this.syncInflightDataLabel(instance, projection)

    const yProjected = this.projectionValueToPixel(instance, actualPoint.y, projection)
    if (yProjected == null) return

    const endPoint = { x: actualPoint.x, y: yProjected }
    const projectionPath = this.buildProjectionPath(instance, projection, yProjected)
    if (!projectionPath) return

    const stroke = this.lineProjectionColor || PROJECTION_LINE_COLOR

    const group = document.createElementNS("http://www.w3.org/2000/svg", "g")
    group.setAttribute("class", "apex-dashboard-chart__projection-overlay")

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
    path.setAttribute("class", "apex-dashboard-chart__projection-line")
    path.setAttribute("d", projectionPath)
    path.setAttribute("stroke", stroke)
    path.setAttribute("fill", "none")
    group.appendChild(path)

    const marker = document.createElementNS("http://www.w3.org/2000/svg", "circle")
    marker.setAttribute("class", "apex-dashboard-chart__projection-marker")
    marker.setAttribute("cx", String(endPoint.x))
    marker.setAttribute("cy", String(endPoint.y))
    marker.setAttribute("r", "3.5")
    marker.setAttribute("stroke", stroke)
    group.appendChild(marker)

    if (projection.projectedValue != null) {
      const label = document.createElementNS("http://www.w3.org/2000/svg", "text")
      label.setAttribute("class", "apex-dashboard-chart__projection-label")
      label.setAttribute("text-anchor", "middle")
      label.setAttribute("x", String(endPoint.x))
      label.setAttribute("y", String(endPoint.y - 12))
      label.textContent = `~${Number(projection.projectedValue).toLocaleString("fr-FR")}`
      group.appendChild(label)
    }

    const inner = instance.el.querySelector(".apexcharts-inner")
    const seriesLayer = inner?.querySelector(".apexcharts-area-series, .apexcharts-line-series")
    if (inner && seriesLayer) {
      inner.insertBefore(group, seriesLayer)
    } else {
      inner?.appendChild(group)
    }
  }

  syncInflightDataLabel(instance, projection) {
    if (!instance?.el || projection?.index == null) return

    const labels = instance.el.querySelectorAll(".apexcharts-datalabel")
    labels.forEach((label, index) => {
      label.classList.remove("apex-dashboard-chart__inflight-datalabel")
      label.removeAttribute("transform")

      const background = label.previousElementSibling
      if (background?.classList?.contains("apex-dashboard-chart__inflight-datalabel-bg")) {
        background.classList.remove("apex-dashboard-chart__inflight-datalabel-bg")
        background.removeAttribute("transform")
      }

      if (index !== projection.index) return

      label.classList.add("apex-dashboard-chart__inflight-datalabel")
      label.setAttribute("transform", `translate(0, ${INFLIGHT_DATA_LABEL_DELTA_Y})`)

      if (background?.tagName === "rect") {
        background.classList.add("apex-dashboard-chart__inflight-datalabel-bg")
        background.setAttribute("transform", `translate(0, ${INFLIGHT_DATA_LABEL_DELTA_Y})`)
      }
    })
  }

  buildLineTooltipHtml({
    series,
    dataPointIndex,
    categories,
    projection,
    projectionLabel,
    seriesTitle,
    color,
    projectionColor
  }) {
    const title = categories[dataPointIndex] || ""
    const actualValue = series?.[0]?.[dataPointIndex]
    const rows = []

    if (actualValue != null && !Number.isNaN(actualValue)) {
      rows.push(`
        <div class="apexcharts-tooltip-series-group apex-dashboard-chart__tooltip-row" style="display: flex;">
          <span class="apexcharts-tooltip-marker apex-dashboard-chart__tooltip-marker" style="background-color: ${color};"></span>
          <div class="apexcharts-tooltip-text">
            <div class="apexcharts-tooltip-y-group">
              <span class="apexcharts-tooltip-text-y-label">${seriesTitle}: </span>
              <span class="apexcharts-tooltip-text-y-value">${actualValue.toLocaleString("fr-FR")}</span>
            </div>
          </div>
        </div>
      `)
    }

    if (dataPointIndex === projection.index && projection.projectedValue != null) {
      rows.push(`
        <div class="apexcharts-tooltip-series-group apex-dashboard-chart__tooltip-row" style="display: flex;">
          <span class="apexcharts-tooltip-marker apex-dashboard-chart__tooltip-marker apex-dashboard-chart__tooltip-marker--projection" style="background-color: ${projectionColor};"></span>
          <div class="apexcharts-tooltip-text">
            <div class="apexcharts-tooltip-y-group">
              <span class="apexcharts-tooltip-text-y-label">${projectionLabel}: </span>
              <span class="apexcharts-tooltip-text-y-value">~${projection.projectedValue.toLocaleString("fr-FR")}</span>
            </div>
          </div>
        </div>
      `)
    }

    if (!rows.length) return ""

    return `
      <div class="apexcharts-tooltip-title">${title}</div>
      ${rows.join("")}
    `
  }

  barView(points) {
    const sorted = [...points].sort((a, b) => Number(b.value) - Number(a.value))
    let filtered = sorted

    if (this.selectedRangeValue === "top5") filtered = sorted.slice(0, 5)
    if (this.selectedRangeValue === "top10") filtered = sorted.slice(0, 10)

    return {
      categories: filtered.map((point) => point.label),
      values: filtered.map((point) => Number(point.value) || 0)
    }
  }

  parseDate(value) {
    if (!value) return null
    if (value instanceof Date) return value

    const raw = String(value)
    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (isoMatch) {
      const [, year, month, day] = isoMatch.map(Number)
      return new Date(year, month - 1, day)
    }

    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  formatMonthLabel(date) {
    const month = MONTH_SHORT_FR[date.getMonth()] || String(date.getMonth() + 1)
    return `${month} ${String(date.getFullYear()).slice(-2)}`
  }

  niceStep(rawStep) {
    if (!rawStep || rawStep <= 0) return 1

    const magnitude = 10 ** Math.floor(Math.log10(rawStep))
    const normalized = rawStep / magnitude
    let nice = 1
    if (normalized <= 1) nice = 1
    else if (normalized <= 2) nice = 2
    else if (normalized <= 5) nice = 5
    else nice = 10

    return nice * magnitude
  }

  buildNiceTicks(lo, hi, segments = 2) {
    const span = Math.max(hi - lo, 1)
    const step = this.niceStep(span / segments)
    const start = Math.floor(lo / step) * step
    const ticks = []

    for (let tick = start; tick <= hi + step * 0.001; tick += step) {
      if (tick >= lo - step * 0.001) ticks.push(Math.round(tick))
    }

    return [...new Set(ticks)].slice(0, 4)
  }

  axisLabelFor(value, ticks, span) {
    if (value == null || Number.isNaN(value)) return ""

    const tolerance = Math.max(span * 0.06, 1)
    const match = ticks.find((tick) => Math.abs(value - tick) <= tolerance)
    if (match == null) return ""

    return match.toLocaleString("fr-FR")
  }
}
